'use client'

import { z } from 'zod'
import { create } from 'zustand'

import { characterForbidden } from '@/features/studio-renew/lib/character-forbidden'
import {
  emptyWork as blankChat,
  hasWorkValues,
  WORK_DRAFT_ID,
} from '@/features/studio-renew/lib/work-draft'
import {
  acceptCreated,
  acceptDeleted,
  acceptDetached,
  acceptRecoveredWorkDraft,
  acceptRecoveredMaterialDraft,
  beginMaterialDraft,
  beginWorkDraft,
  discardMaterialDraft,
  isLocalMaterialDraft,
  savedMaterialId,
  studioServerId,
} from '@/features/studio-renew/sync'
import { isBlankDraft, useRenewStore } from '@/features/studio-renew/store'
import {
  emptyMaterial,
  isMaterialDraftId,
} from '@/features/studio-renew/lib/material-draft'
import { ApiError, api } from '@/features/veluvit/services/api'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { Kind } from '@/features/studio-renew/store'
import type {
  Character,
  Chat,
  Command,
  StatusBoard,
  World,
} from '@/features/studio-renew/types'

type MaterialKind = Exclude<Kind, 'chat'>
type DetachableKind = 'status' | 'command'
type Scope = { owner: string; identity: number }

const PATH: Record<Kind, string> = {
  world: '/creator/me/world/',
  character: '/creator/me/character/',
  status: '/creator/me/status/',
  command: '/creator/me/command/',
  chat: '/creator/me/work/',
}

const idSchema = z.string().min(1)
const materialBase = z.looseObject({ id: idSchema })
const RESPONSE = {
  world: materialBase.extend({
    name: z.string(),
    summary: z.string(),
    cover: z.string(),
    body: z.string(),
    keywords: z.array(z.unknown()),
    places: z.array(z.unknown()),
    visibility: z.string(),
  }),
  character: materialBase.extend({
    name: z.string(),
    image: z.string(),
    tagline: z.string(),
    persona: z.string(),
    worldMode: z.string(),
    worldId: z.string().nullable(),
    worldNote: z.string(),
    forbidden: z.array(z.string()),
    habits: z.array(z.unknown()),
  }),
  status: materialBase.extend({
    name: z.string(),
    fields: z.array(z.unknown()),
    html: z.string(),
  }),
  command: materialBase.extend({
    key: z.string(),
    title: z.string(),
    description: z.string(),
    html: z.string(),
  }),
  chat: z.looseObject({
    id: idSchema,
    work: z.record(z.string(), z.unknown()),
    reviewStatus: z.string(),
    published: z.boolean(),
    version: z.number(),
  }),
} as const
const detachedSchema = z.object({
  material: z.unknown(),
  workId: idSchema,
  work: z.unknown(),
})
type CreationAction = 'create' | 'copy' | 'detach'
type Pending = {
  scope: Scope
  kind: Kind
  action: CreationAction
  path: string
  body: Record<string, unknown>
  requestId: string
  supportsReplay: boolean
  serverId?: string
  workId?: string
  softDraftBody?: string
  localDraftId?: string
  needsReview?: boolean
}
const pendingRequests = new Map<string, Pending>()
const locks = new Set<string>()
export const useCreationRecovery = create<{ pending: Pending[] }>(() => ({
  pending: [],
}))
function publishPending() {
  useCreationRecovery.setState({
    pending: [...pendingRequests.values()].filter((item) => item.needsReview),
  })
}
function remember(scope: Scope, kind: Kind, pending: Pending) {
  pendingRequests.set(requestKey(scope, kind), pending)
  publishPending()
}
function forget(scope: Scope, kind: Kind) {
  pendingRequests.delete(requestKey(scope, kind))
  publishPending()
}
function flagPending(scope: Scope, kind: Kind) {
  const pending = pendingRequests.get(requestKey(scope, kind))
  if (pending) remember(scope, kind, { ...pending, needsReview: true })
}

function currentScope(): Scope {
  const session = useSessionStore.getState()
  if (session.data?.config.creator?.enabled === false)
    throw new ApiError(
      '지금은 제작 기능을 이용할 수 없어요.',
      403,
      'CREATOR_DISABLED'
    )
  const user = session.data?.user
  const owner = useRenewStore.getState().owner
  if (!user || user.guest || !owner || owner !== user.id)
    throw new ApiError(
      '현재 회원 계정을 확인하지 못했어요.',
      403,
      'MEMBER_REQUIRED'
    )
  return { owner, identity: session.identity }
}

function assertScope(scope: Scope) {
  const session = useSessionStore.getState()
  if (
    session.identity !== scope.identity ||
    session.data?.user?.id !== scope.owner ||
    useRenewStore.getState().owner !== scope.owner
  ) {
    throw new ApiError(
      '계정이 바뀌어 작업 결과를 반영하지 않았어요.',
      409,
      'SESSION_CHANGED'
    )
  }
}

async function locked<T>(
  scope: Scope,
  kind: Kind,
  run: () => Promise<T>
): Promise<T> {
  const key = `${scope.owner}:${kind}`
  if (locks.has(key)) throw new Error('같은 종류의 저장 작업이 진행 중이에요.')
  locks.add(key)
  try {
    return await run()
  } finally {
    locks.delete(key)
  }
}

function requestKey(scope: Scope, kind: Kind) {
  return `${scope.owner}:${scope.identity}:${kind}`
}
function supportsReceipt() {
  return (
    useSessionStore.getState().data?.config.creator?.creationReceipts === true
  )
}

function parseCreated(
  kind: Kind,
  value: unknown
): Record<string, unknown> & { id: string } {
  const parsed = RESPONSE[kind].safeParse(value)
  if (!parsed.success)
    throw new ApiError(
      '서버가 만든 항목의 본문을 확인하지 못했어요. 기존 저장 상태를 먼저 확인해 주세요.',
      502,
      'INVALID_STUDIO_CREATE'
    )
  return parsed.data
}

/** 접수 상태를 작은 로컬 기록으로 보존하고, 응답이 불확실하면 같은 종류의 새 POST를 막는다. */
async function adoptCreated(
  kind: Kind,
  serverId: string,
  scope: Scope,
  pending?: Pending,
  recovering = false
) {
  assertScope(scope)
  const created = parseCreated(
    kind,
    await api<unknown>(`${PATH[kind]}${encodeURIComponent(serverId)}/`)
  )
  assertScope(scope)
  if (created.id !== serverId)
    throw new ApiError(
      '생성 응답과 서버 조회 ID가 달라 결과를 반영하지 않았어요.',
      502,
      'INVALID_STUDIO_CREATE'
    )
  acceptCreated(kind, created)
  if (kind !== 'chat' && pending?.localDraftId) {
    const { requestId: _requestId, ...sentBody } = pending.body
    acceptRecoveredMaterialDraft(kind, pending.localDraftId, serverId, sentBody)
  }
  if (recovering && kind === 'chat' && pending?.softDraftBody)
    acceptRecoveredWorkDraft(serverId, pending.softDraftBody)
  forget(scope, kind)
  return serverId
}

async function postCreated(
  kind: Kind,
  path: string,
  body: Record<string, unknown>,
  action: CreationAction,
  scope: Scope,
  alreadyLocked = false,
  softDraftBody?: string,
  localDraftId?: string
): Promise<string> {
  const run = async () => {
    assertScope(scope)
    if (pendingRequests.has(requestKey(scope, kind)))
      throw new Error(
        '이전 저장 결과가 불확실해 새 요청을 보내지 않았어요. 서버 상태를 먼저 확인해 주세요.'
      )
    assertScope(scope)
    const requestId = crypto.randomUUID()
    const pending: Pending = {
      scope,
      kind,
      action,
      path,
      body: structuredClone({ ...body, requestId }),
      requestId,
      supportsReplay:
        (kind === 'chat' && action === 'create') || supportsReceipt(),
      softDraftBody,
      localDraftId,
    }
    remember(scope, kind, pending)
    try {
      const value = await api<unknown>(path, pending.body, 'POST')
      assertScope(scope)
      const serverId = z.object({ id: idSchema }).parse(value).id
      remember(scope, kind, { ...pending, serverId })
      return await adoptCreated(kind, serverId, scope, pending)
    } catch (error) {
      if (
        error instanceof ApiError &&
        !error.unknownOutcome &&
        error.status >= 400 &&
        error.status < 500 &&
        !pendingRequests.get(requestKey(scope, kind))?.serverId
      )
        forget(scope, kind)
      else flagPending(scope, kind)
      throw error
    }
  }
  return alreadyLocked ? run() : locked(scope, kind, run)
}

function withoutLocal<
  T extends {
    id?: string
    updatedAt?: number
    revision?: string
    internalReview?: unknown
    publication?: unknown
  },
>(
  value: T
): Omit<T, 'id' | 'updatedAt' | 'revision' | 'internalReview' | 'publication'> {
  const {
    id: _id,
    updatedAt: _updatedAt,
    revision: _revision,
    internalReview: _internalReview,
    publication: _publication,
    ...body
  } = value
  return body
}

export async function createWorld(patch: Partial<World> = {}): Promise<string> {
  const scope = currentScope()
  return locked(scope, 'world', async () => {
    const body = { ...emptyMaterial('world'), ...withoutLocal(patch) }
    if (isBlankDraft('world', body)) return beginMaterialDraft('world', body)
    assertScope(scope)
    return postCreated('world', PATH.world, body, 'create', scope, true)
  })
}

export async function createCharacter(
  patch: Partial<Character> = {}
): Promise<string> {
  const scope = currentScope()
  return locked(scope, 'character', async () => {
    const body = { ...emptyMaterial('character'), ...withoutLocal(patch) }
    if (isBlankDraft('character', body))
      return beginMaterialDraft('character', body)
    const forbidden = characterForbidden(body.forbidden, body.name)
    if (forbidden.error) throw new Error(forbidden.error)
    body.forbidden = forbidden.values
    assertScope(scope)
    return postCreated('character', PATH.character, body, 'create', scope, true)
  })
}

export async function createStatus(
  patch: Partial<StatusBoard> = {}
): Promise<string> {
  const scope = currentScope()
  return locked(scope, 'status', async () => {
    const body = { ...emptyMaterial('status'), ...withoutLocal(patch) }
    if (isBlankDraft('status', body)) return beginMaterialDraft('status', body)
    assertScope(scope)
    return postCreated('status', PATH.status, body, 'create', scope, true)
  })
}

export async function createCommand(
  patch: Partial<Command> = {}
): Promise<string> {
  const scope = currentScope()
  return locked(scope, 'command', async () => {
    const body = { ...emptyMaterial('command'), ...withoutLocal(patch) }
    if (isBlankDraft('command', body))
      return beginMaterialDraft('command', body)
    assertScope(scope)
    return postCreated('command', PATH.command, body, 'create', scope, true)
  })
}

async function selectedWorld(
  characterIds: string[],
  scope: Scope
): Promise<string | null> {
  for (const id of characterIds) {
    idSchema.parse(id)
    const cached = useRenewStore.getState().characters[id]
    if (cached) {
      if (cached.worldId) return cached.worldId
      continue
    }
    const fetched = z
      .object({ id: idSchema, worldId: z.string().nullable() })
      .safeParse(
        await api<unknown>(`${PATH.character}${encodeURIComponent(id)}/`)
      )
    assertScope(scope)
    if (!fetched.success || fetched.data.id !== id)
      throw new Error('고른 캐릭터의 서버 정보를 확인하지 못했어요.')
    if (fetched.data.worldId) return fetched.data.worldId
  }
  return null
}

export async function createChat(
  characterIds: string[] = [],
  patch: Partial<Chat> = {}
): Promise<string> {
  const scope = currentScope()
  return locked(scope, 'chat', async () => {
    const chosen = await Promise.all(
      (characterIds.length ? characterIds : (patch.characterIds ?? [])).map(
        (id) => (isMaterialDraftId(id) ? savedMaterialId('character', id) : id)
      )
    )
    const worldId = await selectedWorld(chosen, scope)
    assertScope(scope)
    const body = {
      ...blankChat(chosen, worldId),
      ...withoutLocal(patch),
      characterIds: chosen,
    }
    if (!hasWorkValues({ ...body, id: WORK_DRAFT_ID, updatedAt: 0 }))
      return beginWorkDraft()
    if (body.lead && !chosen.includes(body.lead))
      throw new Error('주인공이 출연 캐릭터 목록에 없어요.')
    const {
      audience,
      audienceLocked: _audienceLocked,
      review: _review,
      rejectReason: _rejectReason,
      submittedAt: _submittedAt,
      ...work
    } = body
    assertScope(scope)
    return postCreated(
      'chat',
      PATH.chat,
      {
        schema: 'studio-renew',
        work: audience == null ? work : { ...work, audience },
      },
      'create',
      scope,
      true
    )
  })
}

/** 입력한 재료를 sync가 처음 서버에 저장할 때만 호출한다. */
export async function persistMaterialDraft(
  kind: MaterialKind,
  body: Record<string, unknown>,
  localDraftId: string
): Promise<string> {
  const scope = currentScope()
  if (
    !isLocalMaterialDraft(kind, localDraftId) ||
    isBlankDraft(kind, { ...emptyMaterial(kind), ...body })
  )
    throw new Error('내용을 입력한 뒤 저장해 주세요.')
  if (
    locks.has(`${scope.owner}:${kind}`) ||
    pendingRequests.has(requestKey(scope, kind))
  )
    throw new ApiError(
      '이전 항목의 저장 결과를 확인한 뒤 다시 저장해 주세요. 이번 생성 요청은 보내지 않았어요.',
      409,
      'CREATOR_CREATE_NOT_SENT'
    )
  return postCreated(
    kind,
    PATH[kind],
    body,
    'create',
    scope,
    false,
    undefined,
    localDraftId
  )
}

/** sync가 값이 있는 로컬 작품을 검증·미디어 변환한 뒤에만 호출한다. */
export async function persistWorkDraft(
  work: Record<string, unknown>
): Promise<string> {
  const scope = currentScope()
  if (
    !hasWorkValues({
      ...blankChat(),
      ...work,
      id: WORK_DRAFT_ID,
      updatedAt: 0,
    } as Chat)
  )
    throw new Error('내용을 입력한 뒤 저장해 주세요.')
  const { audience, ...rest } = work
  return postCreated(
    'chat',
    PATH.chat,
    { schema: 'studio-renew', work: audience == null ? rest : work },
    'create',
    scope,
    false,
    JSON.stringify(work)
  )
}

export async function copyMaterial(
  kind: MaterialKind,
  id: string
): Promise<string> {
  const scope = currentScope()
  const savedId = isMaterialDraftId(id) ? await savedMaterialId(kind, id) : id
  assertScope(scope)
  return postCreated(
    kind,
    `${PATH[kind]}${encodeURIComponent(idSchema.parse(savedId))}/copy/`,
    {},
    'copy',
    scope
  )
}

export async function copyChat(id: string): Promise<string> {
  if (id === WORK_DRAFT_ID)
    throw new Error('내용을 입력해 작품을 저장한 뒤 복제해 주세요.')
  const scope = currentScope()
  return postCreated(
    'chat',
    `${PATH.chat}${encodeURIComponent(idSchema.parse(id))}/copy/`,
    {},
    'copy',
    scope
  )
}

async function adoptDetached(
  kind: DetachableKind,
  materialId: string,
  workId: string,
  scope: Scope
) {
  const [material, work] = await Promise.all([
    api<unknown>(`${PATH[kind]}${encodeURIComponent(materialId)}/`),
    api<unknown>(`${PATH.chat}${encodeURIComponent(workId)}/`),
  ])
  assertScope(scope)
  const created = parseCreated(kind, material)
  const updated = parseCreated('chat', work)
  if (created.id !== materialId || updated.id !== workId)
    throw new ApiError(
      '분리 결과의 서버 ID가 달라 적용하지 않았어요.',
      502,
      'INVALID_DETACH_RESULT'
    )
  acceptDetached(kind, { material: created, workId, work: updated })
  forget(scope, kind)
  return materialId
}

export async function detachMaterial(
  workId: string,
  kind: DetachableKind,
  id: string
): Promise<{ materialId: string; workId: string }> {
  const scope = currentScope()
  return locked(scope, kind, async () => {
    assertScope(scope)
    if (pendingRequests.has(requestKey(scope, kind)))
      throw new Error('이전 저장 결과가 불확실해 복사 요청을 보내지 않았어요.')
    const path = `${PATH.chat}${encodeURIComponent(idSchema.parse(workId))}/material/copy/`
    const revision = useRenewStore.getState().chats[workId]?.revision
    const body = {
      kind,
      id: idSchema.parse(id),
      ...(revision ? { baseRevision: revision } : {}),
    }
    const requestId = crypto.randomUUID()
    const pending: Pending = {
      scope,
      kind,
      action: 'detach',
      path,
      body: structuredClone({ ...body, requestId }),
      requestId,
      workId,
      supportsReplay: supportsReceipt(),
    }
    assertScope(scope)
    remember(scope, kind, pending)
    try {
      const value = await api<unknown>(path, pending.body, 'POST')
      const envelope = detachedSchema.parse(value)
      if (envelope.workId !== workId)
        throw new ApiError(
          '분리한 작품 응답의 ID가 달라 결과를 반영하지 않았어요.',
          502,
          'INVALID_DETACH_RESULT'
        )
      const materialId = z.object({ id: idSchema }).parse(envelope.material).id
      remember(scope, kind, { ...pending, serverId: materialId })
      await adoptDetached(kind, materialId, workId, scope)
      return { materialId, workId }
    } catch (error) {
      if (
        error instanceof ApiError &&
        !error.unknownOutcome &&
        error.status >= 400 &&
        error.status < 500 &&
        !pendingRequests.get(requestKey(scope, kind))?.serverId
      )
        forget(scope, kind)
      else flagPending(scope, kind)
      throw error
    }
  })
}

/** 사용자가 이전 요청을 확인할 때만 같은 본문·requestId로 재조회한다. */
export async function recoverCreation(kind: Kind): Promise<string> {
  const scope = currentScope()
  return locked(scope, kind, async () => {
    const pending = pendingRequests.get(requestKey(scope, kind))
    if (
      !pending ||
      pending.scope.owner !== scope.owner ||
      pending.scope.identity !== scope.identity
    )
      throw new Error('확인할 생성 요청이 없어요.')
    assertScope(scope)
    try {
      if (pending.serverId)
        return pending.action === 'detach'
          ? await adoptDetached(
              kind as DetachableKind,
              pending.serverId,
              pending.workId!,
              scope
            )
          : await adoptCreated(kind, pending.serverId, scope, pending, true)
      if (!pending.supportsReplay)
        throw new Error(
          '이 요청은 서버의 중복 방지 지원을 확인하지 못했어요. 목록에서 생성 결과를 먼저 확인해 주세요.'
        )
      const value = await api<unknown>(
        pending.path,
        structuredClone(pending.body),
        'POST'
      )
      assertScope(scope)
      const serverId =
        pending.action === 'detach'
          ? z
              .object({
                material: z.object({ id: idSchema }),
                workId: idSchema,
              })
              .parse(value).material.id
          : z.object({ id: idSchema }).parse(value).id
      remember(scope, kind, { ...pending, serverId })
      return pending.action === 'detach'
        ? await adoptDetached(
            kind as DetachableKind,
            serverId,
            pending.workId!,
            scope
          )
        : await adoptCreated(kind, serverId, scope, pending, true)
    } catch (error) {
      if (
        error instanceof ApiError &&
        !error.unknownOutcome &&
        error.status >= 400 &&
        error.status < 500 &&
        !pendingRequests.get(requestKey(scope, kind))?.serverId
      )
        forget(scope, kind)
      else flagPending(scope, kind)
      throw error
    }
  })
}

export async function deleteMaterial(
  kind: MaterialKind,
  id: string
): Promise<void> {
  const scope = currentScope()
  const savedId = studioServerId(kind, id)
  if (isLocalMaterialDraft(kind, id) && savedId === id) {
    discardMaterialDraft(kind, id)
    return
  }
  await locked(scope, kind, async () => {
    assertScope(scope)
    await api(
      `${PATH[kind]}${encodeURIComponent(idSchema.parse(savedId))}/`,
      undefined,
      'DELETE'
    )
    assertScope(scope)
    acceptDeleted(kind, savedId)
  })
}

export async function archiveChat(id: string): Promise<void> {
  if (id === WORK_DRAFT_ID)
    throw new Error('로컬 초안은 기기에서 정리해 주세요.')
  const scope = currentScope()
  await locked(scope, 'chat', async () => {
    assertScope(scope)
    await api(
      `${PATH.chat}${encodeURIComponent(idSchema.parse(id))}/archive/`,
      { reason: '작가 삭제' }
    )
    assertScope(scope)
    acceptDeleted('chat', id)
  })
}
